'use client';

import {
  PropsWithChildren,
  useEffect,
  useMemo,
  useState,
  useTransition,
} from 'react';
import { InteractionType } from '@azure/msal-browser';
import { MsalAuthenticationTemplate } from '@azure/msal-react';
import { useSWRConfig } from 'swr';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { type UserProfileDto } from '@/authApiClient';
import { msalConfig } from '@/authConfig';
import { Error } from './Error';
import { Loading } from './Loading';
import { PostLoginFlow } from './PostLoginFlow';
import { PostSignUpFlow } from './PostSignUpFlow';
import { PrivateContextProvider } from './PrivateContext';
import {
  useMsalTokenAcquisition,
  useMsalLoginSuccessCallback,
  useDatadogCorrelation,
  useInitializeContext,
  useRoleRedirect,
  usePrivateContextState,
  useDatadogUser,
} from '@/hooks';
import { useStoredTokenValidation } from '@/hooks/private/useStoredTokenValidation';
import { useLaunchDarklyUser } from '@/hooks/useLaunchDarklyUser';
import { CircularProgress } from '@/primitives';
import { tokenStorage } from '@/services/tokenStorage';

interface PrivateProps extends PropsWithChildren {
  onRenderPublicSearchStateChange?: (shouldRenderPublicSearch: boolean) => void;
}

export function Private({
  children,
  onRenderPublicSearchStateChange = () => {},
}: PrivateProps) {
  const { mutate } = useSWRConfig();
  const [loadedUserProfile, setLoadedUserProfile] = useState<UserProfileDto>();
  const [azureAccessToken, setAzureAccessToken] = useState<string>();

  // Get feature flag for stored token authentication
  const { useStoredTokenAuth = false } = useFlags();

  // Always call the hook to satisfy React rules, but we'll use its results conditionally
  const storedTokenValidation = useStoredTokenValidation();

  // Use stored token validation results only if feature flag is enabled
  const {
    isValidating: isValidatingStoredTokens,
    hasValidTokens,
    token: storedToken,
    refreshToken: storedRefreshToken,
    shouldUseMsal,
    error: tokenValidationError,
  } = useStoredTokenAuth
    ? storedTokenValidation
    : {
        isValidating: false,
        hasValidTokens: false,
        token: undefined,
        refreshToken: undefined,
        shouldUseMsal: true,
        error: undefined,
      };

  // Track if we're using stored tokens
  const [usingStoredTokens, setUsingStoredTokens] = useState(false);

  const {
    userOrganisations,
    setUserOrganisations,
    selectedOrganisationId,
    setSelectedOrganisationId,
    setShowSplashScreen,
    contextData,
    setContextData,
  } = usePrivateContextState();

  const [isPending, startTransition] = useTransition();

  // Only trigger MSAL if no valid stored tokens or feature flag is disabled
  const shouldTriggerMsal =
    !isValidatingStoredTokens &&
    (shouldUseMsal || !useStoredTokenAuth) &&
    !usingStoredTokens;

  const callbacks = useMemo(
    () => ({
      onClearCredentials: () => {
        if (usingStoredTokens) {
          setUsingStoredTokens(false);
          setContextData((prevData) => ({
            ...prevData,
            token: undefined,
            refreshToken: undefined,
          }));
        }

        tokenStorage.clearTokens();
      },
      onTokenAcquired: (token: string) => {
        if (shouldTriggerMsal) {
          setAzureAccessToken(token);
        }
      },
    }),
    [shouldTriggerMsal, usingStoredTokens, setContextData],
  );

  useMsalTokenAcquisition(callbacks);

  // Handle stored tokens - only if feature flag is enabled
  useEffect(() => {
    if (
      useStoredTokenAuth &&
      !isValidatingStoredTokens &&
      hasValidTokens &&
      storedToken &&
      storedRefreshToken &&
      !usingStoredTokens
    ) {
      console.log(
        '[Auth] Using stored tokens for authentication (feature flag enabled)',
      );
      setUsingStoredTokens(true);

      // Set the stored tokens in context
      setContextData((prevData) => ({
        ...prevData,
        token: storedToken,
        refreshToken: storedRefreshToken,
      }));

      // Trigger the auth flow with a dummy Azure token
      // The actual tokens will be used in useInitializeContext
      setAzureAccessToken('stored-token-placeholder');
    }
  }, [
    useStoredTokenAuth,
    isValidatingStoredTokens,
    hasValidTokens,
    storedToken,
    storedRefreshToken,
    usingStoredTokens,
    setContextData,
  ]);

  useMsalLoginSuccessCallback((state) => {
    setShowSplashScreen(true);
  });
  useDatadogCorrelation();
  useInitializeContext(
    azureAccessToken,
    selectedOrganisationId,
    userOrganisations,
    setContextData,
    setUserOrganisations,
    setSelectedOrganisationId,
  );
  useDatadogUser(loadedUserProfile, contextData, selectedOrganisationId);
  useLaunchDarklyUser(); // Update LaunchDarkly with user information
  const handleUserProfileLoaded = (userProfile: UserProfileDto) => {
    setLoadedUserProfile(userProfile);
    setContextData((prevContextData) => ({
      ...prevContextData,
      userProfile,
      reloadUserProfile: () => {
        mutate(`/api/UserProfile/${prevContextData?.userId}`);
      },
    }));
  };
  const redirectByRole = useRoleRedirect(
    selectedOrganisationId,
    onRenderPublicSearchStateChange,
  );

  useEffect(() => {
    if (!isPending) {
      setShowSplashScreen(false);
    }
  }, [isPending, setShowSplashScreen]);

  // Show loading state while validating stored tokens - only if feature flag is enabled
  if (useStoredTokenAuth && isValidatingStoredTokens) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <CircularProgress />
      </div>
    );
  }

  return (
    <MsalAuthenticationTemplate
      interactionType={InteractionType.Redirect}
      authenticationRequest={{
        scopes: msalConfig.auth.scopes,
        state:
          typeof window !== 'undefined'
            ? `${window.location.pathname}${window.location.search}`
            : '',
      }}
      errorComponent={Error}
      loadingComponent={Loading}
    >
      <PrivateContextProvider value={contextData}>
        <PostLoginFlow
          onUserProfileLoaded={handleUserProfileLoaded}
          onUserOrgAssociationsRetrieved={redirectByRole}
        />
        <PostSignUpFlow onFinishFlow={redirectByRole} />
        {children}
      </PrivateContextProvider>
    </MsalAuthenticationTemplate>
  );
}
